import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";

import "../../../../style/addrestoration.css";
import "../../../../style/addmaterial.css";
import "../../../../style/dashboardcentralhubstyle.css";
import "../../../../style/customresponsivefile.css";
import "../../../../style/schedulemaster.css";

import { apiAxios } from "../../../../utils/apiAxios";

interface ScheduleMaster {
  scheduleID: number;
  scheduleName: string;
  durationMinutes: number;
  slotIntervalMinutes: number;
  startTime: string;
  endTime: string;
  maxCapacity: number;
  isActive: boolean;
}

interface ScheduleForm {
  scheduleName: string;
  durationMinutes: number;
  slotIntervalMinutes: number;
  startTime: string;
  endTime: string;
  maxCapacity: number;
  isActive: boolean;
}

const ScheduleMasterPage: React.FC = () => {
  const [schedules, setSchedules] = useState<ScheduleMaster[]>([]);

  const [form, setForm] = useState<ScheduleForm>({
    scheduleName: "",
    durationMinutes: 90,
    slotIntervalMinutes: 30,
    startTime: "09:00",
    endTime: "18:00",
    maxCapacity: 5,
    isActive: true,
  });

  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [editId, setEditId] = useState<number | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);

  const [searchText, setSearchText] = useState("");

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const API_BASE = process.env.REACT_APP_API_BASE_URL;

  // --------------------------------------------------
  // Load schedules
  // --------------------------------------------------

const loadSchedules = async () => {
  try {
    setLoading(true);

    const response = await apiAxios.get(
      `${API_BASE}/ScheduleMaster/GetAll`
    );

    console.log("Schedule API Response:", response.data);

    if (
      response.data?.isSuccess ||
      response.data?.IsSuccess ||
      response.data?.StatusCode === 200 ||
      response.data?.statusCode === 200
    ) {
      const scheduleData =
        response.data?.singleData ||
        response.data?.SingleData ||
        [];

      console.log("Schedule Data:", scheduleData);

      setSchedules(scheduleData);
    } else {
      toast.error(
        response.data?.status ||
        response.data?.Status ||
        response.data?.message ||
        response.data?.Message ||
        "Unable to load schedules."
      );
    }
  } catch (error) {
    console.error("Get schedules error:", error);
    toast.error("Unable to load schedules.");
  } finally {
    setLoading(false);
  }
};

  useEffect(() => {
    loadSchedules();
  }, []);

  // --------------------------------------------------
  // Form helpers
  // --------------------------------------------------

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]:
        type === "number"
          ? Number(value)
          : type === "checkbox"
            ? (e.target as HTMLInputElement).checked
            : value,
    }));
  };

  const resetForm = () => {
    setForm({
      scheduleName: "",
      durationMinutes: 90,
      slotIntervalMinutes: 30,
      startTime: "09:00",
      endTime: "18:00",
      maxCapacity: 5,
      isActive: true,
    });

    setEditId(null);
    setIsEditMode(false);
  };

  // --------------------------------------------------
  // Validation
  // --------------------------------------------------

  const validateForm = () => {
    if (!form.scheduleName.trim()) {
      toast.error("Schedule name is required.");
      return false;
    }

    if (form.durationMinutes <= 0) {
      toast.error("Duration must be greater than 0.");
      return false;
    }

    if (form.slotIntervalMinutes <= 0) {
      toast.error("Slot interval must be greater than 0.");
      return false;
    }

    if (form.maxCapacity <= 0) {
      toast.error("Maximum capacity must be greater than 0.");
      return false;
    }

    if (form.startTime >= form.endTime) {
      toast.error("End time must be greater than start time.");
      return false;
    }

    return true;
  };

  // --------------------------------------------------
  // Add / Update
  // --------------------------------------------------

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setIsSubmitting(true);

      if (isEditMode && editId !== null) {
        const payload = {
          scheduleID: editId,
          scheduleName: form.scheduleName.trim(),
          durationMinutes: form.durationMinutes,
          slotIntervalMinutes: form.slotIntervalMinutes,
          startTime: form.startTime,
          endTime: form.endTime,
          maxCapacity: form.maxCapacity,
          isActive: form.isActive,
        };

       const response = await apiAxios.put(
  `${API_BASE}/ScheduleMaster/Update`,
  payload
);

        if (
          response.data?.isSuccess ||
          response.data?.StatusCode === 200
        ) {
          toast.success("Schedule updated successfully.");
          resetForm();
          await loadSchedules();
        } else {
          toast.error(
            response.data?.message || "Unable to update schedule."
          );
        }
      } else {
        const payload = {
          scheduleName: form.scheduleName.trim(),
          durationMinutes: form.durationMinutes,
          slotIntervalMinutes: form.slotIntervalMinutes,
          startTime: form.startTime,
          endTime: form.endTime,
          maxCapacity: form.maxCapacity,
          isActive: form.isActive,
        };

        const response = await apiAxios.post(
          `${API_BASE}/ScheduleMaster/Create`,
          payload
        );

        if (
          response.data?.isSuccess ||
          response.data?.StatusCode === 200
        ) {
          toast.success("Schedule added successfully.");
          resetForm();
          await loadSchedules();
        } else {
          toast.error(
            response.data?.message || "Unable to add schedule."
          );
        }
      }
    } catch (error: any) {
      console.error("Save schedule error:", error);

      toast.error(
        error?.response?.data?.message ||
          error?.response?.data?.Message ||
          "Something went wrong."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // --------------------------------------------------
  // Edit
  // --------------------------------------------------

  const handleEdit = (schedule: ScheduleMaster) => {
    setForm({
      scheduleName: schedule.scheduleName,
      durationMinutes: schedule.durationMinutes,
      slotIntervalMinutes: schedule.slotIntervalMinutes,
      startTime: schedule.startTime?.substring(0, 5) || "09:00",
      endTime: schedule.endTime?.substring(0, 5) || "18:00",
      maxCapacity: schedule.maxCapacity,
      isActive: schedule.isActive,
    });

    setEditId(schedule.scheduleID);
    setIsEditMode(true);

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  // --------------------------------------------------
  // Delete
  // --------------------------------------------------

  const openDeleteModal = (id: number) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setDeleteId(null);
    setShowDeleteModal(false);
  };

  const handleDelete = async () => {
    if (!deleteId) {
      return;
    }

    try {
      setIsSubmitting(true);

      const response = await apiAxios.delete(
        `${API_BASE}/ScheduleMaster/Delete/${deleteId}`
      );

      if (
        response.data?.isSuccess ||
        response.data?.StatusCode === 200
      ) {
        toast.success("Schedule deleted successfully.");

        closeDeleteModal();

        await loadSchedules();
      } else {
        toast.error(
          response.data?.message || "Unable to delete schedule."
        );
      }
    } catch (error: any) {
      console.error("Delete schedule error:", error);

      toast.error(
        error?.response?.data?.message ||
          error?.response?.data?.Message ||
          "Unable to delete schedule."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // --------------------------------------------------
  // Search
  // --------------------------------------------------

  const filteredSchedules = schedules.filter((schedule) => {
    const search = searchText.trim().toLowerCase();

    if (!search) {
      return true;
    }

    return (
      schedule.scheduleName.toLowerCase().includes(search) ||
      String(schedule.durationMinutes).includes(search) ||
      String(schedule.slotIntervalMinutes).includes(search)
    );
  });

  // --------------------------------------------------
  // Format time
  // --------------------------------------------------

  const formatTime = (time: string) => {
    if (!time) {
      return "";
    }

    const [hours, minutes] = time.substring(0, 5).split(":");

    const hour = Number(hours);

    const suffix = hour >= 12 ? "PM" : "AM";
    const displayHour = hour % 12 || 12;

    return `${displayHour}:${minutes} ${suffix}`;
  };

  // --------------------------------------------------
  // JSX
  // --------------------------------------------------

  return (
    <div className="container-fluid pt-3 schedule-master-page">

      {/* ============================================= */}
      {/* PAGE HEADER */}
      {/* ============================================= */}

      <div className="schedule-page-header mb-3">
        <div>
          <h3 className="schedule-page-title">
            Schedule Master
          </h3>

          <p className="schedule-page-subtitle">
            Configure booking duration, slot intervals, working
            hours and maximum capacity.
          </p>
        </div>
      </div>

      {/* ============================================= */}
      {/* ADD / EDIT FORM */}
      {/* ============================================= */}

      <div className="premium-card mb-4">

        <div className="schedule-card-header">
          <div>
            <h5 className="mb-1">
              {isEditMode
                ? "Edit Schedule"
                : "Add Schedule"}
            </h5>

            <span>
              {isEditMode
                ? "Update the selected schedule configuration."
                : "Create a new schedule configuration."}
            </span>
          </div>

          {isEditMode && (
            <button
              type="button"
              className="btn py-2 px-3 btn-secondary"
              onClick={resetForm}
            >
              Cancel Edit
            </button>
          )}
        </div>

        <form onSubmit={handleSubmit}>

          {/* Basic Information */}

          <div className="schedule-section-title">
            Basic Information
          </div>

          <div className="row">

            {/* Schedule Name */}

            <div className="col-md-6 mb-3">
              <label className="form-label">
                Schedule Name <span className="text-danger">*</span>
              </label>

              <input
                type="text"
                name="scheduleName"
                className="form-control"
                placeholder="e.g. 90 Minute Express"
                value={form.scheduleName}
                onChange={handleChange}
                maxLength={100}
              />
            </div>

            {/* Duration */}

            <div className="col-md-3 mb-3">
              <label className="form-label">
                Duration <span className="text-danger">*</span>
              </label>

              <div className="input-group">
                <input
                  type="number"
                  name="durationMinutes"
                  className="form-control"
                  min={1}
                  value={form.durationMinutes}
                  onChange={handleChange}
                />

                <span className="input-group-text">
                  Minutes
                </span>
              </div>
            </div>

            {/* Slot Interval */}

            <div className="col-md-3 mb-3">
              <label className="form-label">
                Slot Interval <span className="text-danger">*</span>
              </label>

              <div className="input-group">
                <input
                  type="number"
                  name="slotIntervalMinutes"
                  className="form-control"
                  min={1}
                  value={form.slotIntervalMinutes}
                  onChange={handleChange}
                />

                <span className="input-group-text">
                  Minutes
                </span>
              </div>
            </div>

          </div>

          {/* Booking Hours */}

          <div className="schedule-section-title mt-2">
            Booking Hours
          </div>

          <div className="row">

            {/* Start Time */}

            <div className="col-md-4 mb-3">
              <label className="form-label">
                Start Time <span className="text-danger">*</span>
              </label>

              <input
                type="time"
                name="startTime"
                className="form-control"
                value={form.startTime}
                onChange={handleChange}
              />
            </div>

            {/* End Time */}

            <div className="col-md-4 mb-3">
              <label className="form-label">
                End Time <span className="text-danger">*</span>
              </label>

              <input
                type="time"
                name="endTime"
                className="form-control"
                value={form.endTime}
                onChange={handleChange}
              />
            </div>

            {/* Capacity */}

            <div className="col-md-4 mb-3">
              <label className="form-label">
                Maximum Capacity{" "}
                <span className="text-danger">*</span>
              </label>

              <input
                type="number"
                name="maxCapacity"
                className="form-control"
                min={1}
                value={form.maxCapacity}
                onChange={handleChange}
              />
            </div>

          </div>

          {/* Status */}

          <div className="schedule-section-title mt-2">
            Status
          </div>

          <div className="schedule-status-row mb-3">

            <label className="schedule-switch">

              <input
                type="checkbox"
                name="isActive"
                checked={form.isActive}
                onChange={handleChange}
              />

              <span className="schedule-slider"></span>

            </label>

            <div>
              <strong>
                {form.isActive ? "Active" : "Inactive"}
              </strong>

              <small>
                {form.isActive
                  ? " This schedule is available for booking."
                  : " This schedule will not be available for booking."}
              </small>
            </div>

          </div>

          {/* Buttons */}

          <div className="schedule-form-actions">

            <button
              type="button"
              className="btn btn-secondary schedule-cancel-btn"
              onClick={resetForm}
              disabled={isSubmitting}
            >
              Clear
            </button>

            <button
              type="submit"
              className="btn btn-primary schedule-save-btn"
              disabled={isSubmitting}
            >
              {isSubmitting
                ? "Saving..."
                : isEditMode
                  ? "Update Schedule"
                  : "Save Schedule"}
            </button>

          </div>

        </form>
      </div>

      {/* ============================================= */}
      {/* LIST */}
      {/* ============================================= */}

      <div className="premium-card">

        <div className="schedule-list-header">

          <div>
            <h5 className="mb-1">
              Schedule List
            </h5>

            <span>
              Manage all booking schedules.
            </span>
          </div>

          <div className="schedule-search-box">

            <i className="bi bi-search"></i>

            <input
              type="text"
              className="form-control"
              placeholder="Search schedule..."
              value={searchText}
              onChange={(e) =>
                setSearchText(e.target.value)
              }
            />

          </div>

        </div>

        <div className="table-responsive">

          <table className="table table-hover align-middle schedule-table custom-material-table">

            <thead>
              <tr>
                <th>#</th>
                <th>Schedule Name</th>
                <th>Duration</th>
                <th>Slot Interval</th>
                <th>Working Hours</th>
                <th>Capacity</th>
                <th>Status</th>
                <th className="text-center">
                  Action
                </th>
              </tr>
            </thead>

            <tbody>

              {loading ? (

                <tr>
                  <td
                    colSpan={8}
                    className="text-center py-5"
                  >
                    Loading schedules...
                  </td>
                </tr>

              ) : filteredSchedules.length === 0 ? (

                <tr>
                  <td
                    colSpan={8}
                    className="text-center py-5"
                  >
                    No schedules found.
                  </td>
                </tr>

              ) : (

                filteredSchedules.map(
                  (schedule, index) => (

                    <tr key={schedule.scheduleID}>

                      <td>
                        {index + 1}
                      </td>

                      <td>
                        <div className="schedule-name-cell">

                          <div className="schedule-icon">
                            <i className="bi bi-clock"></i>
                          </div>

                          <div>
                            <strong>
                              {schedule.scheduleName}
                            </strong>

                            <small>
                              Schedule ID:{" "}
                              {schedule.scheduleID}
                            </small>
                          </div>

                        </div>
                      </td>

                      <td>
                        <span className="schedule-value-badge">
                          {schedule.durationMinutes} min
                        </span>
                      </td>

                      <td>
                        {schedule.slotIntervalMinutes} min
                      </td>

                      <td>
                        <div className="working-hours">
                          <strong>
                            {formatTime(schedule.startTime)}
                          </strong>

                          <span> - </span>

                          <strong>
                            {formatTime(schedule.endTime)}
                          </strong>
                        </div>
                      </td>

                      <td>
                        {schedule.maxCapacity}
                      </td>

                      <td>

                        <span
                          className={
                            schedule.isActive
                              ? "schedule-status active"
                              : "schedule-status inactive"
                          }
                        >
                          <span className="status-dot"></span>

                          {schedule.isActive
                            ? "Active"
                            : "Inactive"}
                        </span>

                      </td>

                      <td>

                        <div className="schedule-actions">

                          <button
                            type="button"
                            className="schedule-action-btn edit"
                            title="Edit"
                            onClick={() =>
                              handleEdit(schedule)
                            }
                          >
                            <i className="bi bi-pencil-square"></i>
                          </button>

                          <button
                            type="button"
                            className="schedule-action-btn delete"
                            title="Delete"
                            onClick={() =>
                              openDeleteModal(
                                schedule.scheduleID
                              )
                            }
                          >
                            <i className="bi bi-trash"></i>
                          </button>

                        </div>

                      </td>

                    </tr>

                  )
                )

              )}

            </tbody>

          </table>

        </div>

      </div>

      {/* ============================================= */}
      {/* DELETE MODAL */}
      {/* ============================================= */}

      <Modal
        show={showDeleteModal}
        onHide={closeDeleteModal}
        centered
      >

        <Modal.Header closeButton>
          <Modal.Title>
            Delete Schedule
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>

          <div className="schedule-delete-content">

            <div className="schedule-delete-icon">
              <i className="bi bi-exclamation-triangle"></i>
            </div>

            <div>

              <h6>
                Are you sure?
              </h6>

              <p>
                Are you sure you want to delete this
                schedule?
              </p>

              <small>
                If this schedule is already being used
                by bookings, deletion should be avoided.
              </small>

            </div>

          </div>

        </Modal.Body>

        <Modal.Footer>

          <button
            type="button"
            className="btn py-2 px-3 btn-secondary"
            onClick={closeDeleteModal}
            disabled={isSubmitting}
          >
            Cancel
          </button>

          <button
            type="button"
            className="btn btn-danger"
            onClick={handleDelete}
            disabled={isSubmitting}
          >
            {isSubmitting
              ? "Deleting..."
              : "Delete"}
          </button>

        </Modal.Footer>

      </Modal>

    </div>
  );
};

export default ScheduleMasterPage;